import { DsmPackageListItem } from './dsm-package.models';

/** Global = options that apply to every model and elevation, ModelSpecific = the rest. Matches `DsmCatalogOptionScope`. */
export enum DsmCatalogOptionScope {
  All = 'All',
  Global = 'Global',
  ModelSpecific = 'ModelSpecific'
}

/** Where an option is sold. Matches `ProductCatalogType`. */
export enum DsmCatalogChannel {
  SalesOffice = 'SalesOffice',
  DesignCenter = 'DesignCenter'
}

/** The home plan a catalog read is for, with names for the header — matches `DsmCatalogContextDto`. */
export interface DsmCatalogContext {
  projectId: string;
  projectName: string;
  modelId: string;
  modelName: string;
  elevationId: string;
  elevationCode: string;
  /** Set when the catalog is for a lot. */
  lotNo?: string | null;
}

/** A category or subcategory lookup — matches `OptionFilterValueDto`. */
export interface DsmCatalogFilterValue {
  id: string;
  code: string;
  description: string;
  /** The DSM label from the Products Catalogue setup, else the library name. */
  effectiveLabel: string;
  isHidden: boolean;
  /** A subcategory's parent; null on categories. */
  categoryId: string | null;
}

/** Matches `OptionFilterRoomDto`. */
export interface DsmCatalogFilterRoom {
  id: string;
  name: string;
  code: string;
}

/** Matches `DsmCatalogFiltersDto`. With several home plans, `filters` is the union of their lookups. */
export interface DsmCatalogFilters {
  context: DsmCatalogContext;
  /** Every home plan read, in the order the list shows them. */
  homes: DsmCatalogContext[];
  filters: {
    categories: DsmCatalogFilterValue[];
    subcategories: DsmCatalogFilterValue[];
    rooms: DsmCatalogFilterRoom[];
    hasAnyOptions: boolean;
  };
}

export interface DsmCatalogRoom {
  id: string;
  name: string;
}

/** e.g. Colour: White, Grey */
export interface DsmCatalogAttribute {
  name: string;
  values: string[];
}

/**
 * One option row — matches `DsmCatalogOptionDto` minus its lot/tax fields, as this page never picks a lot.
 * `rooms`, `attributes` and `imageUrl` stay null unless their include flag was sent.
 */
export interface DsmCatalogOption {
  modelElevationProductId: string;
  productId: string;
  code: string;
  description: string | null;
  categoryId: string | null;
  categoryLabel: string | null;
  subcategoryId: string | null;
  subcategoryLabel: string | null;
  productType: string | null;
  productClass: string | null;
  uofm: { id: string; code: string | null; description: string | null } | null;
  quantity: number;
  /** null when the option has no current price. */
  unitPrice: number | null;
  extendedPrice: number | null;
  appliesToAllLocations: boolean;
  isGlobal: boolean;
  salesOffice: boolean;
  designCenter: boolean;
  isKit: boolean;
  linkedProducts: DsmCatalogOption[];
  rooms: DsmCatalogRoom[] | null;
  attributes: DsmCatalogAttribute[] | null;
  imageUrl: string | null;
}

/**
 * A section of the list, as the PDF prints it: "Model: 37B1 (A)", or "All models / elevations" for the global
 * options every chosen plan shares. Matches `DsmCatalogPageSectionDto`.
 */
export interface DsmCatalogPageSection {
  title: string;
  home: DsmCatalogContext;
  allModels: boolean;
  totalCount: number;
  /** The plan's active packages, by name, as the PDF prints them. Sent with page 1 only (null on later pages); none for All models. */
  packages: DsmCatalogPackage[] | null;
}

/** A package as the Packages list gives it (same totals), plus its options by code. Matches `DsmCatalogPackageDto`. */
export interface DsmCatalogPackage extends DsmPackageListItem {
  options: DsmCatalogPackageOption[];
}

/** One option of a package, `count` times over. Matches `DsmCatalogPackageOptionDto`. */
export interface DsmCatalogPackageOption {
  option: DsmCatalogOption;
  count: number;
  /** `option.extendedPrice` × `count`, before the package discount. */
  amount: number | null;
}

/**
 * Matches `DsmCatalogPageDto`. The options page through the sections laid end to end: the first section's
 * `totalCount` rows are its own, then the next section's, and so on.
 */
export interface DsmCatalogPage {
  context: DsmCatalogContext;
  sections: DsmCatalogPageSection[];
  options: { items: DsmCatalogOption[]; totalCount: number };
}

/** Everything an options or PDF read is filtered by. Lists left empty filter nothing. */
export interface DsmCatalogQuery {
  projectId: string;
  /** The first chosen plan, which the API reads as the context. */
  modelId: string;
  elevationId: string;
  /** Every chosen plan, as `modelId:elevationId`. */
  modelElevations: string[];
  categoryIds: string[];
  subcategoryIds: string[];
  roomIds: string[];
  productTypeIds: string[];
  optionScope: DsmCatalogOptionScope;
  /** Left empty, the API shows Design Center options only. */
  catalogChannels: DsmCatalogChannel[];
  includeRooms: boolean;
  includeAttributes: boolean;
  includeImages: boolean;
}

export type DsmCatalogContextQuery = Pick<DsmCatalogQuery, 'projectId' | 'modelId' | 'elevationId' | 'modelElevations'>;

export interface DsmCatalogPdfQuery extends DsmCatalogQuery {
  hideUnitPrices: boolean;
  hideExtendedPrices: boolean;
}

/** The generated PDF and the file name the API gave it. */
export interface DsmCatalogPdf {
  blob: Blob;
  fileName: string;
}
